var _slicedToArray = function () { function sliceIterator(arr, i) { var _arr = []; var _n = true; var _d = false; var _e = undefined; try { for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"]) _i["return"](); } finally { if (_d) throw _e; } } return _arr; } return function (arr, i) { if (Array.isArray(arr)) { return arr; } else if (Symbol.iterator in Object(arr)) { return sliceIterator(arr, i); } else { throw new TypeError("Invalid attempt to destructure non-iterable instance"); } }; }();

function _objectWithoutProperties(obj, keys) { var target = {}; for (var i in obj) { if (keys.indexOf(i) >= 0) continue; if (!Object.prototype.hasOwnProperty.call(obj, i)) continue; target[i] = obj[i]; } return target; }

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import { useSpacing } from './../globalSpacing';
import makeStyles from './../makeStyles';

var useStyles = makeStyles(function (theme) {
    return {
        root: {
            gridTemplateColumns: 'minmax(0, 1fr)',
            '& .eds-tabs-container button:first-child': {
                marginLeft: 0
            }
        }
    };
});

/**
 * Layout model that provides a big picture of available options and allows users to quickly alternate views within the same context.
 * @done true
 * @updated true
 * @template true
 * @versionAdded v5.5.0
 * @examples
 *  TabLayoutExample
 *  TabLayoutWithTable
 */
var TabLayout = React.forwardRef(function (props, ref) {
    var classes = useStyles();

    var children = props.children,
        spacingOptionsLocal = props.spacingOptions,
        className = props.className,
        rest = _objectWithoutProperties(props, ['children', 'spacingOptions', 'className']);

    var _useSpacing = useSpacing(spacingOptionsLocal),
        _useSpacing2 = _slicedToArray(_useSpacing, 3),
        spacingStyles = _useSpacing2[0],
        setSpacingOptions = _useSpacing2[2];

    var _useState = useState(null),
        _useState2 = _slicedToArray(_useState, 2),
        localChildren = _useState2[0],
        setLocalChildren = _useState2[1];

    useEffect(function () {
        setSpacingOptions(spacingOptionsLocal);
    }, [spacingOptionsLocal]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(function () {
        var childrenMap = React.Children.map(children, function (child) {
            var localChild = child.type.muiName === 'Tabs' ? React.cloneElement(child, {
                scrollButtonsOnMobile: true,
                scrollButtons: true
            }) : child;

            return localChild;
        });

        setLocalChildren(childrenMap);
    }, [children]);

    return React.createElement(
        'div',
        Object.assign({}, rest, {
            className: cn(spacingStyles, className, classes.root),
            ref: ref
        }),
        localChildren
    );
});

TabLayout.muiName = 'TabLayout';

TabLayout.propTypes = {
    /**
     * A CSS class that will be applied to the parent `div`.
     */
    className: PropTypes.string,
    /**
     * Override or extend the styles applied to the component.
     */
    classes: PropTypes.object,
    /**
     * The content of the component.
     */
    children: PropTypes.node.isRequired,
    /**
     * Provides several attributes to control `TabLayout` spacing.
     *
     * Available options are:
     *
     * * `spacing` (string): **'standard'** | 'none' | \<object\>
     *      * Controls the padding of the `TabLayout` and grid gap between child elements
     *      * `standard`: The default spacing
     *      * `none`: Removes all spacing styles, allowing for complete style customization through the `classes` prop
     *      * `object`: Allows for more fine-grained spacing customization. See [container customization documentation](#/utilities/spacing#using-customizations-for-fine-grained-container-spacing-control)
     *        for more details.
     */
    spacingOptions: PropTypes.shape({
        spacing: PropTypes.oneOfType([PropTypes.string, PropTypes.object])
    })
};

export default TabLayout;